import {
    ApiBaseService,
    type PaginatedFetch,
    type PaginatedFetchOptions,
} from "./ApiBaseService";
import type { Form, DisplayForm, FormTranslation } from "~/models/Form";
import type { FormField } from "~/models/FormField";
import type { FieldDefinition } from "~/models/Automation";
import type { ApiResponse } from "~/types/ApiResponse";
import type { StoreTemplate, Template } from "~/models/Template";
import {
    faPause,
    faPencil,
    faPlay,
    faTrash,
    faEye,
    faBallot,
    faBoxArchive,
    faClone,
    faFileExport,
    faFileCsv,
    faFileExcel,
} from "@fortawesome/pro-solid-svg-icons";
import type { ExtendedDropdownItem } from "~/components/ui/DropdownItems.vue";
import { useClipboard } from "#imports";
import type { FetchOptions } from "ofetch";
import type { LengthAwarePaginator } from "~/types/LengthAwarePaginator";

export type CreateOrUpdateForm = Partial<
    Omit<Form, "id" | "created_at" | "updated_at" | "deleted_at" | "fields">
> &
    Partial<{
        brand_id: number | null;
        fields: Partial<
            Omit<FormField, "id" | "created_at" | "updated_at" | "deleted_at">
        >[];
    }>;

export interface GenerateFormOptions {
    prompt: string;
    instructions?: string;
    flags?: {
        fake?: boolean;
    };
}

export default class FormService extends ApiBaseService {
    public list(
        options?: Partial<PaginatedFetchOptions>,
    ): PaginatedFetch<DisplayForm> {
        return this.paginate<DisplayForm>(
            this.route("api.forms.index", "/forms"),
            options as PaginatedFetchOptions,
        );
    }

    public listRoute() {
        return this.route("api.forms.index", "/forms");
    }

    public listRequest(options?: FetchOptions): Promise<ApiResponse<LengthAwarePaginator<Form> | Form[]>> {
        return this.request(this.listRoute(), options);
    }

    public store(data?: CreateOrUpdateForm): Promise<ApiResponse<Form>> {
        return this.request(this.route("api.forms.store", "/forms"), {
            method: "POST",
            body: data,
        });
    }

    public generate(options: GenerateFormOptions): Promise<ApiResponse<Form>> {
        return this.request(
            this.route("api.forms.generate", "/forms/generate"),
            {
                method: "POST",
                body: options,
            },
        );
    }

    public generateUpdate(form_id: string, options: GenerateFormOptions): Promise<ApiResponse<Form>> {
        return this.request(
            this.route("api.forms.generate-update", "/forms/{id}/generate-update", {
                id: form_id,
            }),
            {
                method: "POST",
                body: options,
            },
        );
    }

    public generateFields(
        id: string,
        options: GenerateFormOptions,
    ): Promise<ApiResponse<Form>> {
        return this.request(
            this.route("api.forms.fields.generate", "/forms/{id}/generate", {
                id,
            }),
            {
                method: "POST",
                body: options,
            },
        );
    }

    public find(
        id: string,
        options?: FetchOptions,
    ): Promise<ApiResponse<Form>> {
        return this.request(
            this.route("api.forms.show", "/forms/{id}", {
                id,
            }),
            options,
        );
    }

    public listFieldMap(
        id: string,
        data: CreateOrUpdateForm,
    ): Promise<ApiResponse<FieldDefinition[]>> {
        return this.request(
            this.route("api.forms.field_map", "/forms/{id}/fields", {
                id,
            }),
            {
                method: "POST",
                body: data,
            },
        );
    }

    public update(
        id: string,
        data: CreateOrUpdateForm,
    ): Promise<ApiResponse<Form>> {
        data = useCloneDeep(data);

        if ("brand" in data) {
            data.brand_id = data.brand?.id ?? null;
            delete data.brand;
        }

        if ("locale" in data) {
            data.locale_id = data.locale?.id ?? null;
            delete data.locale;
        }

        return this.request(
            this.route("api.forms.store", "/forms/{id}", {
                id,
            }),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    async generateTranslation(id: string, locale_id: number) {
        return this.request<
            ApiResponse<Record<string, string | object | null>>
        >(
            this.route(
                "api.forms.generate-translation",
                "/forms/{id}/generate-translation/{locale_id}",
                {
                    id,
                    locale_id,
                },
            ),
            {
                method: "POST",
                body: {
                    locale_id,
                },
            },
        );
    }

    async updateTranslation(id: string, data: Partial<FormTranslation>) {
        return this.request<ApiResponse<FormTranslation>>(
            this.route(
                "api.forms.update-translation",
                "/forms/{id}/update-translation",
                {
                    id,
                },
            ),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    public deleteTranslation(id: string, translation_id: number) {
        return this.request(
            this.route(
                "api.forms.delete-translation",
                "/forms/{id}/delete-translation/{translation_id}",
                {
                    id,
                    translation_id,
                },
            ),
            {
                method: "DELETE",
            },
        );
    }

    public delete(id: string): Promise<null> {
        return this.request(
            this.route("api.forms.destroy", "/forms/{id}", {
                id,
            }),
            {
                method: "DELETE",
            },
        );
    }

    public duplicate(id: string): Promise<ApiResponse<Form>> {
        return this.request(
            this.route("api.forms.duplicate", "/forms/{id}/duplicate", {
                id,
            }),
            {
                method: "POST",
            },
        );
    }

    public showTemplate(id: string): Promise<ApiResponse<Template>> {
        return this.request(
            this.route("api.forms.templates.store", "/forms/{id}/template", {
                id,
            }),
        );
    }

    public storeTemplate(
        id: string,
        data: Partial<StoreTemplate>,
    ): Promise<ApiResponse<Template>> {
        if (!(data.banner instanceof File)) {
            delete data.banner;
        }

        return this.request(
            this.route("api.forms.templates.store", "/forms/{id}/template", {
                id,
            }),
            {
                method: "POST",
                body: jsonToFormData(data, {
                    booleanAsNumbers: true,
                }),
            },
        );
    }

    public disableTemplate(uuid: string): Promise<ApiResponse<Template>> {
        return this.request(
            this.route("api.forms.templates.delete", "/forms/{uuid}/template", {
                uuid,
            }),
            {
                method: "DELETE",
            },
        );
    }

    public enableTemplate(uuid: string): Promise<ApiResponse<Template>> {
        return this.request(
            this.route(
                "api.forms.templates.restore",
                "/forms/{uuid}/template/restore",
                {
                    uuid,
                },
            ),
            {
                method: "POST",
            },
        );
    }

    public export(id: string, extension: string): Promise<ApiResponse<Form>> {
        return this.request(
            this.route("api.forms.export", "/forms/{id}/export/{extension}", {
                id,
                extension,
            }),
            {
                method: "POST",
            },
        );
    }

    public dropdown(
        form: Form | Ref<Form>,
        options: Partial<{
            isEditing?: boolean;
            deletable?: Ref<Form | undefined>;
            forDuplication?: Ref<Form | undefined>;
            onWithdrawal?: () => void;
            onPublish?: () => void;
            onArchive?: () => void;
        }> = {},
    ): Partial<ExtendedDropdownItem>[][] {
        form = isRef(form) ? form : toRef(form);
        const { hasPermissionToAny } = useUser();

        const { copy } = useClipboard({
            legacy: true,
        });

        const items: ExtendedDropdownItem[][] = [
            []
        ];

        if (form.value.is_public && form.value.public_url)
            items[0]!.push({
                label: "Copy Public URL",
                icon: faBallot,
                href: form.value.preview_url,
                target: "_blank",
                click: () => {
                    copy(form.value.public_url);

                    useToast().add({
                        title: `Public URL been copied.`,
                    });
                },
            });

        items[0]!.push({
            label: "Preview",
            icon: faEye,
            href: form.value.preview_url,
            target: "_blank",
            click: () => window.open(form.value.preview_url, "_blank"),
        });

        if (hasPermissionToAny(["forms.write", "forms.manage"])) {
            if (!options.isEditing)
                items[0]!.push({
                    label: "Edit",
                    icon: faPencil,
                    to: {
                        name: "forms-id",
                        params: {
                            id: form.value.id,
                        },
                    },
                });

            items[0]!.push({
                label: "Duplicate",
                icon: faClone,
                click: () => {
                    this.duplicate(form.value.id).then((data) => {
                        useToast().add({
                            title: "Form duplicated",
                            description: "You can now edit the new form",
                            color: "primary",
                        });
                        navigateTo(`/forms/${data.data.id}`);
                    });
                },
            });

            if (form.value.status !== "published") {
                items[0]!.push({
                    label: "Publish",
                    icon: faPlay,
                    click: async () => {
                        if (options.onPublish) return options.onPublish();

                        const status = form.value.status;
                        await this.update(form.value.id, {
                            ...form.value,
                            status: "published",
                        });
                        form.value.status = "published";

                        useToast().add({
                            title: `${form.value.name} has been published.`,
                            actions: [
                                {
                                    label: "Undo",
                                    click: async () => {
                                        await this.update(form.value.id, {
                                            ...form.value,
                                            status: status,
                                        });
                                        form.value.status = status;
                                    },
                                },
                            ],
                        });
                    },
                });
            } else {
                items[0]!.push({
                    label: "Withdraw",
                    icon: faPause,
                    click: async () => {
                        if (options.onWithdrawal) return options.onWithdrawal();
                        const status = form.value.status;
                        await this.update(form.value.id, {
                            ...form.value,
                            status: "withdrawn",
                        });
                        form.value.status = "withdrawn";
                        useToast().add({
                            title: `${form.value.name} has been withdrawn.`,
                            actions: [
                                {
                                    label: "Undo",
                                    click: async () => {
                                        await this.update(form.value.id, {
                                            ...form.value,
                                            status: status,
                                        });
                                        form.value.status = status;
                                    },
                                },
                            ],
                        });
                    },
                });
            }

            items[0]!.push({
                label: "Archive",
                icon: faBoxArchive,
                click: async () => {
                    if (options.onArchive) return options.onArchive();

                    form.value.status = "archived";
                    useToast().add({
                        title: "Are you sure?",
                        description: `You're about to archive "${form.value.name ?? ""}."`,
                        actions: [
                            {
                                label: "Yes, archive",
                                color: "primary",
                                click: async () => {
                                    const status = form.value.status;
                                    await this.update(form.value.id, {
                                        ...form.value,
                                        status: "archived",
                                    });

                                    useToast().add({
                                        title: `${form.value.name} has been archived.`,
                                        actions: [
                                            {
                                                label: "Undo",
                                                click: async () => {
                                                    await this.update(
                                                        form.value.id,
                                                        {
                                                            ...form.value,
                                                            status: status,
                                                        },
                                                    );
                                                    form.value.status = status;
                                                },
                                            },
                                        ],
                                    });
                                },
                            },
                            {
                                label: "No, cancel",
                            },
                        ],
                    });
                },
            });
        }

        if (hasPermissionToAny(["forms.manage"])) {
            items[0]!.push({
                label: "Export CSV",
                icon: faFileCsv,
                click: () => {
                    this.export(form.value.id, "csv").then((data) => {
                        useToast().add({
                            icon: "i-heroicons-clock",
                            title: "Export Scheduled",
                            description: "The export is being processed and will be sent to your email.",
                        });
                    }).catch((error) => {
                        useToast().add({
                            icon: "i-heroicons-x-circle",
                            title: "Export Scheduling Failed",
                            description: error.message,
                            color: "red",
                        });
                    });
                },
            });

            items[0]!.push({
                label: "Export Excel",
                icon: faFileExcel,
                click: () => {
                    this.export(form.value.id, "xlsx").then((data) => {
                        useToast().add({
                            icon: "i-heroicons-clock",
                            title: "Export Scheduled",
                            description: "The export is being processed and will be sent to your email.",
                        });
                    }).catch((error) => {
                        useToast().add({
                            icon: "i-heroicons-x-circle",
                            title: "Export Scheduling Failed",
                            description: error.message,
                            color: "red",
                        });
                    });
                },
            });
        }

        if (hasPermissionToAny(["forms.delete", "forms.manage"])) {
            items.push([
                {
                    label: "Delete",
                    class: "text-red-500",
                    iconClass: "text-red-500",
                    icon: faTrash,
                    click: () => {
                        if (options.deletable) {
                            options.deletable.value = form.value;
                        } else {
                            useToast().add({
                                title: "Are you sure?",
                                description: `You're about to delete "${form.value.name ?? ""}."`,
                                actions: [
                                    {
                                        label: "Yes, delete",
                                        color: "primary",
                                        click: async () => {
                                            this.delete(form.value.id).then(
                                                async () => {
                                                    if (
                                                        useRoute().params.id ===
                                                        form.value.id
                                                    ) {
                                                        navigateTo({
                                                            name: "forms",
                                                        });
                                                    } else {
                                                        await refreshNuxtData();
                                                    }
                                                },
                                            );

                                            useToast().add({
                                                title: `${form.value.name} has been deleted.`,
                                            });
                                        },
                                    },
                                    {
                                        label: "No, cancel",
                                    },
                                ],
                            });
                        }
                    },
                },
            ]);
        }

        return items;
    }

    public generateTemplateFieldsSuggestions(id: number|string, data: {
        title: string | undefined;
        description: string | undefined;
        tags: (string | undefined)[];
    }): Promise<ApiResponse<Template & { error?: string }>> {
        return this.request(
            this.route("api.forms.template.generate-fields-suggestions", "/forms/{id}/template/generate-fields-suggestions", {
                id
            }),
            {
                method: "POST",
                body: data,
            }
        );
    }

    public_preview_url_locale(id: string, locale: string) {
        const url = useRuntimeConfig().public.formUrl.replace(/\/$/, '');
        return (
            url +
            this.route("api.public.forms.preview.localized", "/{identifier}/preview/{locale}", {
                identifier: id,
                locale,
            })
        );
    }
}
